import { Chip, type Tone } from '../ui';
import { isPartlyReady, orderStatusText, type Production } from './order-readiness';
import type { ProductionLine } from '../api/types';

/**
 * An order's status chip. For "Partially ready" it also carries a hover card (and a keyboard-focus
 * one) listing each product: how many are ready and how many are still to come, so nobody has to open
 * the order to find out which product is holding it up.
 */
export function ReadinessChip(
  { status, production, tone }: {
    status: string; production: (Production & { lines?: readonly ProductionLine[] }) | null | undefined; tone: Tone;
  },
) {
  const chip = <Chip tone={tone}>{orderStatusText(status, production)}</Chip>;
  // One row per product NAME: variants of the same product are added together (3 sizes of one ring read
  // as one ring with the total made and ordered).
  const byName = new Map<string, { name: string; made: number; ordered: number }>();
  for (const l of production?.lines ?? []) {
    const g = byName.get(l.name) ?? { name: l.name, made: 0, ordered: 0 };
    g.made += l.made;
    g.ordered += l.ordered;
    byName.set(l.name, g);
  }
  const lines = [...byName.values()];
  if (!isPartlyReady(status, production) || lines.length === 0) return chip;
  return (
    <span className="mk-tip" tabIndex={0}>
      {chip}
      <span className="mk-tip-body" role="tooltip">
        {lines.map((l) => (
          <span key={l.name} className="mk-tip-row">
            <span>{l.name}</span>
            <span className={l.made >= l.ordered ? 'mk-tip-ok' : 'mk-tip-wait'}>
              {l.made >= l.ordered ? `${l.made} ready` : `${l.made} ready · ${l.ordered - l.made} to come`}
            </span>
          </span>
        ))}
      </span>
    </span>
  );
}
